import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { whatsappService } from "services/whatsapp";
import { sendDietChartWhatsAppNotification, sendBulkDietChartWhatsAppNotifications } from "services/dietchart";
import { sendInvoiceWhatsAppNotification, sendPaymentReminderWhatsApp } from "services/billing";

// Send diet chart WhatsApp notification
export const sendDietChartNotification = createAsyncThunk(
  "whatsapp/sendDietChartNotification",
  async ({ clientId, dietChartId, options = {} }, thunkAPI) => {
    try {
      const result = await sendDietChartWhatsAppNotification(clientId, dietChartId, options);
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message || "Failed to send diet chart notification"
      });
    }
  }
);

// Send bulk diet chart WhatsApp notifications
export const sendBulkDietChartNotifications = createAsyncThunk(
  "whatsapp/sendBulkDietChartNotifications",
  async ({ clientIds, options = {} }, thunkAPI) => {
    try {
      const result = await sendBulkDietChartWhatsAppNotifications(clientIds, options);
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message || "Failed to send bulk diet chart notifications"
      });
    }
  }
);

// Send invoice WhatsApp notification
export const sendInvoiceNotification = createAsyncThunk(
  "whatsapp/sendInvoiceNotification",
  async ({ clientId, invoiceData, options = {} }, thunkAPI) => {
    try {
      const result = await sendInvoiceWhatsAppNotification(clientId, invoiceData, options);
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message || "Failed to send invoice notification"
      });
    }
  }
);

// Send payment reminder WhatsApp
export const sendPaymentReminder = createAsyncThunk(
  "whatsapp/sendPaymentReminder",
  async ({ clientId, paymentData, options = {} }, thunkAPI) => {
    try {
      const result = await sendPaymentReminderWhatsApp(clientId, paymentData, options);
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message || "Failed to send payment reminder"
      });
    }
  }
);

// Test WhatsApp connection
export const testWhatsAppConnection = createAsyncThunk(
  "whatsapp/testConnection",
  async ({ authKey }, thunkAPI) => {
    try {
      whatsappService.setAuthKey(authKey);
      const result = await whatsappService.getBalance();
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message || "Failed to test WhatsApp connection"
      });
    }
  }
);

// Send test WhatsApp message
export const sendTestMessage = createAsyncThunk(
  "whatsapp/sendTestMessage",
  async ({ authKey, phoneNumber, countryCode, templateId, bodyValues }, thunkAPI) => {
    try {
      whatsappService.setAuthKey(authKey);
      const result = await whatsappService.sendTextMessage({
        mobile: phoneNumber,
        countryCode,
        wid: templateId,
        bodyValues
      });
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message || "Failed to send test message"
      });
    }
  }
);

const whatsappSlice = createSlice({
  name: "whatsapp",
  initialState: {
    settings: {
      authKey: "",
      isEnabled: false,
      testPhoneNumber: "",
      testCountryCode: "91"
    },
    templates: {},
    isFetching: false,
    isSuccess: false,
    isError: false,
    message: null,
    lastNotification: null,
    connectionStatus: null
  },
  reducers: {
    clearState: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
      state.message = null;
      return state;
    },
    
    updateSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    
    updateTemplates: (state, action) => {
      state.templates = { ...state.templates, ...action.payload };
    },
    
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    }
  },
  extraReducers: {
    // Send diet chart notification
    [sendDietChartNotification.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    },
    [sendDietChartNotification.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
      state.lastNotification = payload;
      state.message = payload.message || "Diet chart notification sent successfully";
    },
    [sendDietChartNotification.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.isSuccess = false;
      state.message = payload.message || "Failed to send diet chart notification";
    },

    // Send bulk diet chart notifications
    [sendBulkDietChartNotifications.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    },
    [sendBulkDietChartNotifications.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
      state.lastNotification = payload;
      state.message = payload.message || "Bulk diet chart notifications sent successfully";
    },
    [sendBulkDietChartNotifications.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.isSuccess = false;
      state.message = payload.message || "Failed to send bulk diet chart notifications";
    },

    // Send invoice notification
    [sendInvoiceNotification.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    },
    [sendInvoiceNotification.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
      state.lastNotification = payload;
      state.message = payload.message || "Invoice notification sent successfully";
    },
    [sendInvoiceNotification.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.isSuccess = false;
      state.message = payload.message || "Failed to send invoice notification";
    },

    // Send payment reminder
    [sendPaymentReminder.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    },
    [sendPaymentReminder.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
      state.lastNotification = payload;
      state.message = payload.message || "Payment reminder sent successfully";
    },
    [sendPaymentReminder.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.isSuccess = false;
      state.message = payload.message || "Failed to send payment reminder";
    },

    // Test WhatsApp connection
    [testWhatsAppConnection.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    },
    [testWhatsAppConnection.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
      state.connectionStatus = { connected: true, balance: payload };
      state.message = "WhatsApp connection successful";
    },
    [testWhatsAppConnection.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.isSuccess = false;
      state.connectionStatus = { connected: false, error: payload.message };
      state.message = payload.message || "WhatsApp connection failed";
    },

    // Send test message
    [sendTestMessage.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      state.isSuccess = false;
    },
    [sendTestMessage.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
      state.message = "Test message sent successfully";
    },
    [sendTestMessage.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.isSuccess = false;
      state.message = payload.message || "Failed to send test message";
    }
  }
});

export const { 
  clearState, 
  updateSettings, 
  updateTemplates, 
  setConnectionStatus 
} = whatsappSlice.actions;

export const whatsappSelector = (state) => state.whatsapp;

export default whatsappSlice.reducer;
